import { gql } from "@apollo/client";

export const payerTransactionLogEntryFragment = gql`
  fragment payerTransactionLogEntryFragment on financial_transaction_log {
    transactionId: transaction_id
    firstPark: first_park
    unregisteredVehicleViolation: unregistered_vehicle_violation
    policyViolation: policy_violation
    transactionType: transaction_type
    accountId: payer_account_id
    price
    serviceFeeAmount: service_fee_amount
    gatewayTransactionType: gateway_transaction_type
    gatewayTransactionStatus: gateway_transaction_status
    status
    index
    accountBalance: payer_account_balance
    startTime: start_time
    endTime: end_time
    licensePlateNumber: license_plate_number
    licensePlateStateCode: license_plate_state_code
    addressStreet: address_street
    addressCity: address_city
    addressState: address_state
    transactionTimestamp: transaction_ts
    referenceTransactionId: reference_transaction_id
    referenceTransactionIndex: reference_transaction_index
    referenceTransactionType: reference_transaction_type
    referenceGatewayTransactionType: reference_gateway_transaction_type
    referencedTransaction: referenced_transaction {
      transactionTimestamp: transaction_ts
    }
    # An adjustment's free-text reason (AUT-6158) lives on financial.transaction; the log view
    # doesn't carry it, so it's read through the view's existing transaction relationship.
    transaction {
      id
      description
    }
  }
`;

/**
 * `financial.transaction_type` values that reach the payer transaction log. Not exhaustive (the
 * enum has more values, e.g. `violation`), hence the `string` fallback in the field below.
 */
export type PayerTransactionType = "park" | "trip_charge" | "gateway_transaction" | "adjustment";

type GatewayTransactionType =
  | "initial_wallet_load"
  | "wallet_reload"
  | "payout"
  | "wallet_refund"
  | "session_payment"
  | "permit";

export interface PayerTransactionLogEntry {
  transactionId: string;
  firstPark: boolean;
  unregisteredVehicleViolation: boolean;
  policyViolation: boolean;
  /**
   * AUT-6162: `"adjustment"` rows (chargebacks/credits, PRO-2210) have a null
   * `gatewayTransactionType`, so they must be told apart from park rows by this field.
   */
  transactionType: PayerTransactionType | string;
  accountId: string;
  price: number;
  /**
   * AUT-6146: card-payment service fee in cents, already included in `price` (`price` is the full
   * amount charged to the payment method). Null/0 for fee-less rows — park rows, ACH/wallet-funded
   * charges, and every row written before fees shipped.
   */
  serviceFeeAmount: number | null;
  accountBalance: number;
  startTime: number;
  endTime: number;
  licensePlateNumber: string | null;
  licensePlateStateCode: string | null;
  addressStreet: string;
  addressCity: string;
  addressState: string;
  transactionTimestamp: string;
  gatewayTransactionType: GatewayTransactionType | null;
  gatewayTransactionStatus: "approved" | "declined" | "error" | "held_for_review" | "pending" | null;
  status: string;
  index: number;
  /** AUT-6162: the transaction an adjustment adjusts. All four reference fields are null on non-adjustment rows. */
  referenceTransactionId: string | null;
  referenceTransactionIndex: number | null;
  referenceTransactionType: PayerTransactionType | string | null;
  /** The referenced transaction's gateway type: null for a park reference, else wallet_reload / permit / etc. */
  referenceGatewayTransactionType: GatewayTransactionType | null;
  /**
   * AUT-6162: the referenced row itself, through the `referenced_transaction` Hasura object
   * relationship on the log view (AUT-6160). Only its `transaction_ts` is read: the "Adjusts #…"
   * link opens the table on the reference's month. Null on non-adjustment rows.
   */
  referencedTransaction: { transactionTimestamp: string } | null;
  /**
   * AUT-6162: the row's `financial.transaction`, through the log view's `transaction` object
   * relationship. Only its `description` is read: the free-text reason on adjustment rows (e.g.
   * "Chargeback"), null on other rows. Typed nullable because a relationship row the role can't
   * select comes back as null.
   */
  transaction: { id: string; description: string | null } | null;
}
